import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { TripInfo } from './../models/trip-info.model';
import { returnDateValidator, depositDateValidator, PolicyViewConfig, BaseFieldConfig, departureDateValidator } from '@bhtp-client-web/component';

@Component({
    selector: 'trip-information',
    template: require('./trip-information.component.html')
})
export class TripInformationComponent implements OnChanges {
    @Input() submitted: boolean;
    @Input() tripInfo: TripInfo;
    @Input() configuration: PolicyViewConfig;
    @Input() maxDate: string;
    @Input() form: FormGroup;

    public departureDate: FormControl;
    public returnDate: FormControl;
    public depositDate: FormControl;
    public destinationCountry: FormControl;
    public validationMessages: any = this.getValidationMessages();

    private hasFields: boolean = false;
    private formInitialized: boolean;

    private get maxTripLength(): number {
        if (this.configuration) {
            return this.configuration.tripDates.maxTripLength;
        } else {
            return 0;
        }
    }

    private get minTripLength(): number {
        if (this.configuration && this.configuration.tripDates && this.configuration.tripDates.minTripLength) {
            return this.configuration.tripDates.minTripLength;
        } else {
            return 0;
        }
    }

    constructor() { }

    public ngOnChanges(changes: SimpleChanges): void {
        if ((changes['form'] || changes['tripInfo']) && (this.form && this.tripInfo)) {
            this.buildForm();
        }

        if (changes['maxDate'] || changes['configuration']) {
            this.validationMessages = this.getValidationMessages();
        }
    }

    public buildForm(): void {
        let departureDateValidators: ValidatorFn[] = this.getDepartureDateValidators(this.configuration.tripDates);
        this.departureDate = new FormControl(this.tripInfo.departureDate || '', Validators.compose(departureDateValidators));
        this.form.addControl('departureDate', this.departureDate);

        let returnDateValidators: ValidatorFn[] = this.getReturnDateValidators(this.configuration.tripDates, this.departureDate, this.maxDate);
        this.returnDate = new FormControl(this.tripInfo.returnDate, Validators.compose(returnDateValidators));
        this.form.addControl('returnDate', this.returnDate);

        this.departureDate.valueChanges.subscribe(newValue => {
            // when the value of the departure date control changes, run
            // validation on return date control
            this.returnDate.setValidators(this.getReturnDateValidators(this.configuration.tripDates, this.departureDate, this.maxDate));
            this.returnDate.updateValueAndValidity();
        });

        let depositDateValidators: ValidatorFn[] = [];
        if (this.configuration.depositDate.required) {
            depositDateValidators.push(Validators.required);
        }

        depositDateValidators.push(depositDateValidator());
        this.depositDate = new FormControl(this.tripInfo.depositDate || '', Validators.compose(depositDateValidators));
        this.form.addControl('depositDate', this.depositDate);

        // initialize to whole country, otherwise start with an empty string
        this.destinationCountry = new FormControl(
            this.tripInfo.destinationCountry && this.tripInfo.destinationCountry.name ? this.tripInfo.destinationCountry : '',
            this.configuration.destination.required ? [Validators.required] : []);
        this.form.addControl('destinationCountry', this.destinationCountry);

        if (this.configuration.depositDate.show ||
            this.configuration.tripDates.show ||
            this.configuration.destination.show)
        {
            this.hasFields = true;
        }

        this.formInitialized = true;
    }

    private getReturnDateValidators(config: BaseFieldConfig, departureDateControl: AbstractControl, maxDate: string): ValidatorFn[] {
        let validators: ValidatorFn[] = [];

        if (config.required) {
            validators.push(Validators.required);
        }

        validators.push(returnDateValidator(departureDateControl, maxDate));

        return validators;
    }

    private getDepartureDateValidators(config: BaseFieldConfig): ValidatorFn[] {
        let validators: ValidatorFn[] = [];

        if (config.required) {
            validators.push(Validators.required);
        }

        validators.push(departureDateValidator());

        return validators;
    }

    private getValidationMessages(): any {
        return {
            'departureDate': {
                'required': 'Departure Date is required.',
                'invalid': 'Departure Date must be a valid date in mm/dd/yyyy format.',
                'afterMax': 'Departure Date must be on or before ' + this.maxDate + '.',
                'beforeToday': 'Departure Date must be after today.'
            },
            'returnDate': {
                'required': 'Return Date is required.',
                'invalid': 'Return Date must be a valid date in mm/dd/yyyy format.',
                'beforeDeparture': 'Return Date must be on or after Departure Date.',
                'afterMax': 'Return Date must be on or before ' + this.maxDate + '.',
                'maxTripLength': `Trip length can not exceed ${this.maxTripLength} days.`,
                'minTripLength': `Trip length must be at least ${this.minTripLength} days.`,
                'beforeToday': 'Return Date must be after today.'
            },
            'depositDate': {
                'required': 'Deposit Date is required.',
                'invalid': 'Deposit Date must be a valid date in mm/dd/yyyy format.',
                'afterToday': 'Deposit Date must be today or in the past.',
            },
            'destinationCountry': {
                'required': 'Destination Country is required.'
            }
        };
    }
}




// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/purchase/trip-information/trip-information.component.ts